import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowPathIcon, ExclamationTriangleIcon, HomeIcon } from '@heroicons/react/24/outline';
import { reportClientError } from '@/utils/clientErrorReporter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportClientError({
      type: 'react',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
    this.setState({
      error,
      errorInfo,
    });

    // Auto-recovery para chunk errors (deploy nuevo invalida hashes anteriores).
    // Hard-reload una vez por sesión usando sessionStorage como flag anti-loop.
    if (this.isChunkLoadErrorMessage(error.message)) {
      try {
        const FLAG = 'migro:chunk-reload-attempted';
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(FLAG)) {
          sessionStorage.setItem(FLAG, String(Date.now()));
          // Pequeño delay para que el reportClientError tenga tiempo de salir
          setTimeout(() => window.location.reload(), 250);
        }
      } catch {
        /* sessionStorage bloqueado (modo privado, etc.) — el botón manual sigue funcionando */
      }
    }
  }

  isChunkLoadErrorMessage = (msg: string): boolean => {
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      /ChunkLoadError|dynamically imported module/i.test(msg)
    );
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  /** Detecta error de chunk obsoleto (404 tras un nuevo deploy) */
  isChunkLoadError = (): boolean => {
    return this.isChunkLoadErrorMessage(this.state.error?.message ?? '');
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.isChunkLoadError();
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full shadow-lg border-2 border-red-200">
            <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="bg-red-100 p-6 rounded-full">
                    <ExclamationTriangleIcon className="text-red-600" width={64} height={64} />
                  </div>
                </div>
                <CardTitle className="text-3xl text-emphasis-900">
                  {isChunkError ? 'Nueva versión disponible' : '¡Oops! Algo salió mal'}
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              <div className="text-center">
                {isChunkError ? (
                  <>
                    <p className="text-lg text-gray-700">
                      La aplicación se ha actualizado. Recarga la página para cargar la última versión.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Si no recargas, algunas pantallas pueden fallar al cargar.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg text-gray-700">
                      Lo sentimos, ha ocurrido un error inesperado.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Nuestro equipo ha sido notificado y estamos trabajando en solucionarlo.
                    </p>
                  </>
                )}
              </div>

              {/* Detalles del error */}
              {this.state.error && (
                <details className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <summary className="font-semibold text-sm text-gray-700 cursor-pointer">
                    Detalles del error
                  </summary>
                  <pre className="text-xs text-red-600 overflow-auto max-h-60 mt-2 whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {this.state.error.stack && (
                      <>
                        {'\n\n'}
                        {this.state.error.stack}
                      </>
                    )}
                    {this.state.errorInfo && (
                      <>
                        {'\n\n--- Component Stack ---\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </details>
              )}

              {/* Botones de acción: en chunk error priorizar Recargar */}
              <div className="flex gap-4">
                <Button
                  onClick={this.handleReload}
                  variant={isChunkError ? 'default' : 'outline'}
                  className={`flex-1 ${isChunkError ? 'bg-primary hover:bg-primary-700 text-white' : ''}`}
                >
                  <ArrowPathIcon className="mr-2" width={18} height={18} />
                  Recargar Página
                </Button>
                <Button
                  onClick={this.handleReset}
                  variant={isChunkError ? 'outline' : 'default'}
                  className={`flex-1 ${!isChunkError ? 'bg-primary hover:bg-primary-700 text-white' : ''}`}
                >
                  <HomeIcon className="mr-2" width={18} height={18} />
                  Volver al Inicio
                </Button>
              </div>

              {/* Información de soporte */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-800">
                  Si el problema persiste, contacta con nuestro soporte:
                  <br />
                  <a href="mailto:soporte@migro.es" className="font-semibold text-primary hover:underline">
                    soporte@migro.es
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Reset del flag de auto-reload tras un render exitoso. Si la página
    // re-ejecuta y NO hay error, el siguiente deploy también podrá auto-recuperar.
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('migro:chunk-reload-attempted');
      } catch {
        /* ignore */
      }
    }

    return this.props.children;
  }
}

